import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { fileUrl } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { PropertyContextHeader } from "@/components/PropertyContextHeader";
import { CustodyShield } from "@/components/CustodyShield";
import { HelpHint } from "@/components/HelpHint";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { LegalDisclaimer } from "@/context/DisclaimerContext";
import { FeedbackPrompt } from "@/context/FeedbackContext";
import {
  GitCompareArrows, Loader2, LogIn, Sparkles, AlertTriangle,
  CheckCircle2, ChevronDown, ChevronUp, RefreshCw, WifiOff, X, Trash2,
} from "lucide-react";

const CLASS_META = {
  NO_CHANGE: { label: "No Change", cls: "bg-[#D1FAE5] text-[#177245] border-[#A7F3D0]" },
  NORMAL_WEAR: { label: "Normal Wear", cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" },
  NEW_DAMAGE: { label: "New Damage", cls: "bg-[#FEE2E2] text-[#B91C1C] border-[#FECACA]" },
};
const uid = () => Math.random().toString(36).slice(2, 10);
const nameOf = (photo, rooms) => rooms.find((r) => r.room_id === photo.room_id)?.name || "Room";
const isActive = (j) => j.status === "queued" || j.status === "processing";

export default function Compare() {
  const { propertyId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [outRooms, setOutRooms] = useState([]);
  const [outPhotos, setOutPhotos] = useState([]);
  const [inProps, setInProps] = useState([]);
  const [selectedInProp, setSelectedInProp] = useState("");
  const [inRooms, setInRooms] = useState([]);
  const [inPhotos, setInPhotos] = useState([]);
  const [pairs, setPairs] = useState([]);

  const [view, setView] = useState("launcher"); // launcher | results
  const [jobs, setJobs] = useState([]);
  const [running, setRunning] = useState(false);
  const [pollPaused, setPollPaused] = useState(false);
  const pollFails = useRef(0);

  const load = useCallback(async () => {
    const [{ data: p }, { data: ph }, { data: all }, { data: existing }] = await Promise.all([
      api.get(`/properties/${propertyId}`),
      api.get(`/properties/${propertyId}/photos`),
      api.get(`/properties`),
      api.get(`/comparisons/${propertyId}`),
    ]);
    setOutRooms(p.rooms || []);
    setOutPhotos(ph);
    setInProps(all.filter((x) => x.doc_type === "move_in"));
    if (existing.length > 0) { setJobs(existing); setView("results"); }
    setLoading(false);
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const chooseInProp = async (id) => {
    setSelectedInProp(id);
    const [{ data: p }, { data: ph }] = await Promise.all([
      api.get(`/properties/${id}`),
      api.get(`/properties/${id}/photos`),
    ]);
    setInRooms(p.rooms || []);
    setInPhotos(ph);
    // Auto-pair by matching room label across phases
    const inByRoom = {};
    ph.forEach((x) => { (inByRoom[nameOf(x, p.rooms || [])] ||= []).push(x); });
    const outByRoom = {};
    outPhotos.forEach((x) => { (outByRoom[nameOf(x, outRooms)] ||= []).push(x); });
    const next = [];
    Object.entries(outByRoom).forEach(([room, ops]) => {
      const ips = inByRoom[room] || [];
      if (!ips.length) return;
      next.push({ id: uid(), roomLabel: room, inPhotoId: ips[0].photo_id, outPhotoId: ops[0].photo_id });
    });
    setPairs(next);
  };

  const updatePair = (id, key, val) => setPairs((ps) => ps.map((p) => (p.id === id ? { ...p, [key]: val } : p)));
  const removePair = (id) => setPairs((ps) => ps.filter((p) => p.id !== id));

  const fetchJobs = useCallback(async () => {
    try {
      const { data } = await api.get(`/comparisons/${propertyId}`);
      setJobs(data);
      pollFails.current = 0;
    } catch (e) {
      pollFails.current += 1;
      if (pollFails.current >= 3) setPollPaused(true);
    }
  }, [propertyId]);

  const runComparison = async () => {
    if (!pairs.length) return;
    setRunning(true);
    try {
      await api.post(`/comparisons`, {
        property_id: propertyId,
        pairs: pairs.map((p) => ({ move_in_photo_id: p.inPhotoId, move_out_photo_id: p.outPhotoId })),
      });
      setView("results");
      setPollPaused(false);
      pollFails.current = 0;
      fetchJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start comparison");
    } finally {
      setRunning(false);
    }
  };

  const retry = async (job) => {
    try {
      await api.post(`/photos/${job.move_out_photo_id}/retry-processing`, { retry_target: "comparison_analysis" });
      setPollPaused(false);
      pollFails.current = 0;
      fetchJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    }
  };

  const resume = () => { setPollPaused(false); pollFails.current = 0; fetchJobs(); };

  // Poll every 4s while any job is queued/processing; stop otherwise.
  useEffect(() => {
    if (view !== "results" || pollPaused) return;
    if (jobs.length > 0 && !jobs.some(isActive)) return;
    const t = setInterval(fetchJobs, 4000);
    return () => clearInterval(t);
  }, [view, jobs, pollPaused, fetchJobs]);

  if (loading) return <AppShell><Skeleton className="h-96 rounded-2xl" /></AppShell>;

  return (
    <AppShell>
      <PropertyContextHeader propertyId={propertyId} />

      <div className="flex items-center gap-2">
        <GitCompareArrows size={24} className="text-brand" />
        <h1 className="font-heading text-3xl font-bold tracking-tight text-navy">Comparison Report</h1>
        <HelpHint topic="compare" className="ml-auto" />
      </div>
      <p className="mt-2 text-[#475569]">AI pairs your move-in and move-out photos and flags new damage vs normal wear.</p>

      {view === "launcher" ? (
        <Launcher
          inProps={inProps} selectedInProp={selectedInProp} chooseInProp={chooseInProp}
          pairs={pairs} inPhotos={inPhotos} outPhotos={outPhotos} inRooms={inRooms} outRooms={outRooms}
          updatePair={updatePair} removePair={removePair} running={running} runComparison={runComparison}
          hasResults={jobs.length > 0} onViewResults={() => setView("results")}
        />
      ) : (
        <Results
          jobs={jobs} pollPaused={pollPaused} resume={resume} retry={retry}
          onNewComparison={() => setView("launcher")}
        />
      )}
      {jobs.some((j) => j.status === "completed") && <FeedbackPrompt context="comparison_completed" />}
    </AppShell>
  );
}

function Launcher({ inProps, selectedInProp, chooseInProp, pairs, inPhotos, outPhotos, inRooms, outRooms, updatePair, removePair, running, runComparison, hasResults, onViewResults }) {
  if (inProps.length === 0) {
    return (
      <div className="mt-8 rounded-2xl border border-dashed border-[#CBD5E1] bg-white p-8 sm:p-12 text-center" data-testid="compare-empty">
        <span className="mx-auto grid place-items-center h-14 w-14 rounded-2xl bg-amber-soft text-amber"><LogIn size={26} /></span>
        <h2 className="mt-4 font-heading text-xl font-semibold text-navy">No move-in record to compare against</h2>
        <p className="mt-2 text-[#475569] max-w-md mx-auto">Document the property's move-in condition first, then come back to compare.</p>
      </div>
    );
  }
  const inOptions = (roomLabel) => {
    const same = inPhotos.filter((p) => nameOf(p, inRooms) === roomLabel);
    return same.length ? same : inPhotos;
  };
  const outOptions = (roomLabel) => {
    const same = outPhotos.filter((p) => nameOf(p, outRooms) === roomLabel);
    return same.length ? same : outPhotos;
  };

  return (
    <div className="mt-6" data-testid="comparison-launcher">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-sm w-full">
          <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Compare against move-in record</label>
          <Select value={selectedInProp} onValueChange={chooseInProp}>
            <SelectTrigger data-testid="movein-property-select" className="mt-1.5 h-12 rounded-xl bg-white">
              <SelectValue placeholder="Select a move-in property" />
            </SelectTrigger>
            <SelectContent>
              {inProps.map((p) => <SelectItem key={p.property_id} value={p.property_id}>{p.address}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {hasResults && (
          <Button data-testid="view-results-btn" variant="outline" onClick={onViewResults} className="rounded-xl h-11">View existing results</Button>
        )}
      </div>

      {selectedInProp && (
        <>
          {pairs.length === 0 ? (
            <p className="mt-8 text-[#475569]">No matching rooms found between the two records. Make sure room names match across move-in and move-out.</p>
          ) : (
            <div className="mt-6 space-y-4">
              {pairs.map((pair, i) => (
                <div key={pair.id} data-testid={`pair-row-${i}`} className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-heading font-semibold text-navy">{pair.roomLabel}</span>
                    <button data-testid={`remove-pair-${i}`} onClick={() => removePair(pair.id)} className="grid place-items-center h-8 w-8 rounded-lg text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#B91C1C]"><Trash2 size={16} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <PairSide label="Move-In" testId={`pair-in-select-${i}`} value={pair.inPhotoId} options={inOptions(pair.roomLabel)} onChange={(v) => updatePair(pair.id, "inPhotoId", v)} />
                    <PairSide label="Move-Out" testId={`pair-out-select-${i}`} value={pair.outPhotoId} options={outOptions(pair.roomLabel)} onChange={(v) => updatePair(pair.id, "outPhotoId", v)} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <Button
            data-testid="run-comparison-btn"
            onClick={runComparison}
            disabled={!pairs.length || running}
            className="mt-6 w-full sm:w-auto h-12 px-8 rounded-xl bg-brand hover:bg-brand-hover text-white shadow-[0_4px_14px_rgba(15,27,45,0.28)]"
          >
            {running ? <><Loader2 className="animate-spin mr-2" size={18} /> Starting…</> : <><Sparkles size={18} className="mr-2" /> Run Comparison</>}
          </Button>
        </>
      )}
    </div>
  );
}

function PairSide({ label, value, options, onChange, testId }) {
  const selected = options.find((p) => p.photo_id === value);
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8] mb-1.5">{label}</p>
      <div className="aspect-video rounded-xl overflow-hidden border border-[#E2E8F0] bg-[#F1F5F9] mb-2">
        {selected && <img src={fileUrl(selected.storage_path)} alt="" className="w-full h-full object-cover" />}
      </div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="h-10 rounded-lg bg-white text-sm"><SelectValue placeholder="Choose photo" /></SelectTrigger>
        <SelectContent>
          {options.map((p, idx) => <SelectItem key={p.photo_id} value={p.photo_id}>Photo {idx + 1}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Results({ jobs, pollPaused, resume, retry, onNewComparison }) {
  const counts = jobs.reduce((acc, j) => {
    const c = j.result?.classification;
    if (c) acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {});
  const anyActive = jobs.some(isActive);
  const failedCount = jobs.filter((j) => j.status === "failed").length;

  return (
    <div className="mt-6">
      {pollPaused && (
        <div data-testid="resume-polling-banner" className="mb-4 rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 flex items-center justify-between gap-3">
          <span className="flex items-center gap-2 text-sm font-medium text-[#B91C1C]"><WifiOff size={16} /> Connection lost — live updates paused.</span>
          <Button data-testid="resume-btn" size="sm" onClick={resume} className="rounded-lg bg-navy hover:bg-navy-hover text-white">Resume</Button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2" data-testid="comparison-summary">
          <SummaryPill n={counts.NEW_DAMAGE || 0} label="New Damage" cls={CLASS_META.NEW_DAMAGE.cls} />
          <SummaryPill n={counts.NORMAL_WEAR || 0} label="Normal Wear" cls={CLASS_META.NORMAL_WEAR.cls} />
          <SummaryPill n={counts.NO_CHANGE || 0} label="No Change" cls={CLASS_META.NO_CHANGE.cls} />
          {anyActive && <span className="text-sm text-[#94A3B8] flex items-center gap-1.5"><Loader2 size={14} className="animate-spin" /> analyzing…</span>}
          {failedCount > 0 && (
            <span data-testid="batch-failed-summary" className="text-sm font-semibold text-[#B91C1C] flex items-center gap-1.5">
              <AlertTriangle size={14} /> {failedCount} of {jobs.length} photos failed processing
            </span>
          )}
        </div>
        <Button data-testid="new-comparison-btn" variant="outline" onClick={onNewComparison} className="rounded-xl h-11">New comparison</Button>
      </div>

      {jobs.length === 0 ? (
        <p className="mt-8 text-[#475569]">Queuing your comparisons…</p>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 gap-5" data-testid="comparison-grid">
          {jobs.map((job, i) => <ComparisonCard key={job.job_id} job={job} idx={i} onRetry={() => retry(job)} />)}
        </div>
      )}
    </div>
  );
}

function SummaryPill({ n, label, cls }) {
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${cls}`}>{n} {label}</span>;
}

function ComparisonCard({ job, idx, onRetry }) {
  const [open, setOpen] = useState(false);
  const meta = job.result ? CLASS_META[job.result.classification] : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      data-testid={`comparison-card-${idx}`}
      className="rounded-2xl border border-[#E2E8F0] bg-white p-4"
    >
      <div className="flex items-center justify-between">
        <span className="font-heading font-semibold text-navy">{job.room_label}</span>
        <StatusBadge job={job} meta={meta} />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <Thumb label="Move-In" src={job.move_in_image} />
        <Thumb label="Move-Out" src={job.move_out_image}
          custody={job.move_out_custody}
          failed={job.status === "failed"}
          error={job.result?.last_error || job.error_message}
          onRetry={onRetry} />
      </div>

      <div className="mt-3" data-testid={`card-status-${idx}`}>
        {job.status === "processing" && (
          <div className="h-8 rounded-lg bg-[#F1F5F9] animate-pulse" />
        )}
        {job.status === "queued" && (
          <p className="text-sm text-[#94A3B8]">Waiting to be analyzed…</p>
        )}
        {job.status === "completed" && job.result && (
          <div>
            <div className="flex items-center gap-2 text-sm">
              {job.result.confidence != null && (
                <span className="text-[#475569]">Confidence: <span className="font-semibold text-navy">{Math.round(job.result.confidence * 100)}%</span></span>
              )}
              {(job.result.damage_description || (job.result.affected_regions || []).length > 0) && (
                <button data-testid={`toggle-details-${idx}`} onClick={() => setOpen((o) => !o)} className="ml-auto flex items-center gap-1 text-brand font-medium">
                  Details {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              )}
            </div>
            <LegalDisclaimer keyName="ai_analysis" className="mt-2 text-[11px] leading-relaxed text-[#94A3B8]" />
            {open && (
              <div className="mt-3 rounded-xl bg-[#FAF7F2] border border-[#E2E8F0] p-3" data-testid={`details-${idx}`}>
                {job.result.damage_description && <p className="text-sm text-[#334155]">{job.result.damage_description}</p>}
                {(job.result.affected_regions || []).length > 0 && (
                  <ul className="mt-2 space-y-1.5">
                    {job.result.affected_regions.map((r, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-[#334155]">
                        <AlertTriangle size={14} className="text-[#F59E0B] mt-0.5 shrink-0" />
                        <span><span className="font-medium">{r.label}</span>{r.location_description ? ` — ${r.location_description}` : ""}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function StatusBadge({ job, meta }) {
  if (job.status === "queued") return <span data-testid="badge-queued" className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]">Waiting…</span>;
  if (job.status === "processing") return <span data-testid="badge-processing" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-[#EEF2FF] text-navy border-[#C7D2FE] animate-pulse"><Loader2 size={12} className="animate-spin" /> Analyzing…</span>;
  if (job.status === "failed") return <span data-testid="badge-failed" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border border-[#FECACA] text-[#B91C1C] bg-white"><X size={12} /> Analysis failed</span>;
  if (job.status === "completed" && meta) return <span data-testid="badge-completed" className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${meta.cls}`}>{meta.label}</span>;
  return null;
}

function Thumb({ label, src, failed, error, onRetry, custody }) {
  const [retrying, setRetrying] = useState(false);
  const [showErr, setShowErr] = useState(false);
  const doRetry = async () => {
    setRetrying(true);
    try { await onRetry?.(); } finally { setTimeout(() => setRetrying(false), 900); }
  };
  return (
    <div className="relative aspect-video rounded-lg overflow-hidden border border-[#E2E8F0] bg-[#F1F5F9]">
      {src && <img src={fileUrl(src)} alt={label} className="w-full h-full object-cover" />}
      {custody && !failed && <CustodyShield custody={custody} className="absolute top-1 right-1 z-10" />}
      <span className="absolute bottom-1 left-1 rounded bg-black/60 text-white text-[10px] font-semibold px-1.5 py-0.5">{label}</span>
      {failed && (
        <div data-testid="thumb-failed-overlay" className="absolute inset-0 bg-[#7F1D1D]/90 flex flex-col items-center justify-center gap-1 text-white text-center px-2">
          {retrying ? (
            <span className="flex items-center gap-1.5 text-xs font-semibold"><Loader2 size={14} className="animate-spin" /> Retrying…</span>
          ) : (
            <>
              <AlertTriangle size={18} />
              <span className="text-[11px] font-bold leading-tight">Processing Failed</span>
              <button data-testid="thumb-retry-btn" onClick={doRetry} className="mt-0.5 rounded-full bg-white text-[#7F1D1D] text-[11px] font-bold px-3 py-1 hover:bg-white/90 transition-colors">Tap to Retry</button>
              {error && (
                <button data-testid="thumb-why-btn" onClick={() => setShowErr((s) => !s)} className="text-[10px] underline opacity-80 hover:opacity-100">
                  Why did this fail?
                </button>
              )}
              {showErr && error && <span data-testid="thumb-error-detail" className="text-[10px] opacity-90 leading-tight max-w-full break-words">{error}</span>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
